import type { ColDef, ValueFormatterParams, ValueParserParams } from "@ag-grid-community/core";
import { t } from "@lingui/core/macro";
/* eslint-disable class-methods-use-this */
import { EMPTY_ARRAY, endOfDay, noop } from "@regrello/core-utils";
import { truncateToDateString } from "@regrello/core-utils";
import { FeatureFlagKey } from "@regrello/feature-flags-api";
import {
  type CreateFieldInstanceValueInputs,
  type FieldInstanceFields,
  FormConstraintConditionOperator,
  type FormFieldFields,
  PropertyDataType,
  type PropertyTypeFields,
  type SpectrumFieldValidationTypeFields,
  type SpectrumValueConstraintFields,
} from "@regrello/graphql-api";
import { isValid } from "date-fns";
import type { ReactNode } from "react";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";

import { FeatureFlagService } from "../../../../../services/FeatureFlagService";
import { getErrorMessageWithPayload } from "../../../../../utils/getErrorMessageWithPayload";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../views/modals/formDialogs/spectrumFields/_internal/ConfigureSpectrumFieldForm";
import type {
  CustomFieldPlugin,
  GetCreateFieldInstanceValueInputsFromFormValueParams,
} from "../../../customFields/plugins/types/CustomFieldPlugin";
import { RegrelloControlledFormFieldSelect } from "../../../formFields/controlled/regrelloControlledFormFields";
import { RegrelloFormFieldDate } from "../../../formFields/RegrelloFormFieldDate";
import {
  type ProspectiveSpectrumFieldVersion,
  SpectrumFieldPluginDecorator,
} from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";
import {
  FrontendValueConstraintRuleName,
  ON_OR_AFTER_TODAY_ARG,
  ON_OR_BEFORE_TODAY_ARG,
} from "../../utils/spectrumFieldConstraintUtils";
import { RegrelloEditableCellDate } from "./_internal/RegrelloEditableCellDate";
import type { DateFieldPluginFrontendValue } from "./_internal/types";

const ERROR_INVALID_FORM_VALUE = "Provided 'date'-field form value is not a valid date string";

export class SpectrumDateFieldPluginDecorator extends SpectrumFieldPluginDecorator<DateFieldPluginFrontendValue> {
  constructor(plugin: CustomFieldPlugin<DateFieldPluginFrontendValue>) {
    super(plugin);
    this.uri = "com.regrello.spectrumField.date";
  }

  public canProcessValidationType(spectrumValidationType: ProspectiveSpectrumFieldVersion["validationType"]) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.TIMESTAMP;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === PropertyDataType.TIME);
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find(
      (validationType) => validationType.validationType === SpectrumFieldValidationType.TIMESTAMP,
    );
  }

  public findValueConstraintsFromLoadedValueConstraints(valueConstraints: SpectrumValueConstraintFields[]) {
    return valueConstraints.filter(
      ({ valueConstraintRule }) =>
        valueConstraintRule === FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE,
    );
  }

  public override getCreateFieldInstanceValueInputsFromFormValue = (
    inputs: GetCreateFieldInstanceValueInputsFromFormValueParams,
  ): CreateFieldInstanceValueInputs => {
    const { displayOrder, field, inputType, spectrumFieldVersion, value } = inputs;
    if (!isValueValid(value)) {
      throw new Error(getErrorMessageWithPayload(ERROR_INVALID_FORM_VALUE, { field, inputType, value }));
    }

    if (value == null) {
      return {
        fieldId: field.id,
        timeValue: undefined,
        dateValue: undefined,
        inputType,
        displayOrder,
        spectrumFieldVersionId: spectrumFieldVersion?.id,
      };
    }

    return {
      fieldId: field.id,
      timeValue: value != null ? endOfDay(value).toISOString() : undefined,
      dateValue: value != null ? truncateToDateString(value) : undefined,
      inputType,
      displayOrder,
      spectrumFieldVersionId: spectrumFieldVersion?.id,
    };
  };

  public getConstraintConditionOperators() {
    const isDateOnlyFieldEnabled = FeatureFlagService.isEnabled(
      FeatureFlagKey.SPECTRUM_CUSTOM_DATE_FIELDS_AS_DATE_ONLY_FIELDS_2024_11,
    );
    if (isDateOnlyFieldEnabled) {
      return {
        [FormConstraintConditionOperator.IS_ZERO]: {
          label: t`Is empty`,
          inputCount: 0,
          isMultiselect: false,
        },
        [FormConstraintConditionOperator.IS_NOT_ZERO]: {
          label: t`Is not empty`,
          inputCount: 0,
          isMultiselect: false,
        },
        [FormConstraintConditionOperator.DATE_IS_AFTER_OR_ON]: {
          label: t`Is after or on`,
          inputCount: 1,
          isMultiselect: false,
        },
        [FormConstraintConditionOperator.DATE_IS_BEFORE_OR_ON]: {
          label: t`Is before or on`,
          inputCount: 1,
          isMultiselect: false,
        },
        [FormConstraintConditionOperator.DATE_IS_ON]: {
          label: t`Is on same calendar day`,
          inputCount: 1,
          isMultiselect: false,
        },
        [FormConstraintConditionOperator.DATE_IS_NOT_ON]: {
          label: t`Is not on same calendar day`,
          inputCount: 1,
          isMultiselect: false,
        },
      };
    }

    return {
      [FormConstraintConditionOperator.IS_ZERO]: {
        label: t`Is empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.IS_NOT_ZERO]: {
        label: t`Is not empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.GTE]: {
        label: t`Is after or on`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.LTE]: {
        label: t`Is before or on`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.EQ]: {
        label: t`Is on same calendar day`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.NOT]: {
        label: t`Is not on same calendar day`,
        inputCount: 1,
        isMultiselect: false,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onChange" as const;
  }

  public renderPreviewFormField() {
    return <RegrelloFormFieldDate disabled={true} onChange={noop} size="medium" value={undefined} />;
  }

  public renderValueConstraints(props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  }): ReactNode {
    const { constraints, form, disabled } = props;
    if (constraints.length <= 0) {
      return null;
    }
    const options = [
      { constraint: undefined, args: EMPTY_ARRAY },
      { constraint: constraints[0].constraint, args: [ON_OR_AFTER_TODAY_ARG] },
      { constraint: constraints[0].constraint, args: [ON_OR_BEFORE_TODAY_ARG] },
    ];

    return (
      <RegrelloControlledFormFieldSelect
        className="w-full"
        controllerProps={{
          control: form.control,
          name: "valueConstraints.0",
        }}
        disabled={disabled}
        getOptionLabel={(option) => {
          const isOnOrBeforeToday =
            option.constraint?.valueConstraintRule ===
              FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE &&
            option.args.length > 0 &&
            option.args[0] === ON_OR_BEFORE_TODAY_ARG;
          const isOnOrAfterToday =
            option.constraint?.valueConstraintRule ===
              FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE &&
            option.args.length > 0 &&
            option.args[0] === ON_OR_AFTER_TODAY_ARG;

          return isOnOrAfterToday
            ? t`Only dates in the future, including today`
            : isOnOrBeforeToday
              ? t`Only dates in the past, including today`
              : t`All dates`;
        }}
        label={t`Accepted dates`}
        options={options}
      />
    );
  }

  public getColumnDef<RowDatum>(
    fieldInstance: FieldInstanceFields,
    _formField: FormFieldFields,
    colDefParams: Partial<ColDef<RowDatum>>,
  ): ColDef<RowDatum> {
    return {
      ...colDefParams,
      cellClass: "regrello-center-aligned-cell",
      cellEditor: RegrelloEditableCellDate,
      cellEditorParams: {
        fieldInstance,
        isDateOnlyFieldEnabled: FeatureFlagService.isEnabled(
          FeatureFlagKey.SPECTRUM_CUSTOM_DATE_FIELDS_AS_DATE_ONLY_FIELDS_2024_11,
        ),
      },
      cellEditorPopup: true,
      cellEditorPopupPosition: "over", // eslint-disable-line lingui/no-unlocalized-strings
      valueParser: (params: ValueParserParams<RowDatum>) => {
        const value = params.newValue;
        if (value == null) {
          return null;
        }
        const parsedDate: DateFieldPluginFrontendValue = new Date(params.newValue);
        if (isValid(parsedDate)) {
          return parsedDate;
        }
        return null;
      },
      valueFormatter: (params: ValueFormatterParams<RowDatum>) => {
        if (params.value == null) {
          return "";
        }
        if (params.value instanceof Date) {
          const year = params.value.getFullYear();
          const month = String(params.value.getMonth() + 1).padStart(2, "0");
          const day = String(params.value.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        }
        return "";
      },
    };
  }
}

function isValueValid(value: unknown): value is DateFieldPluginFrontendValue {
  return value == null || (value instanceof Date && isValid(value));
}
